import Language from "./Language";
import Log from "./Log";
import MessageDispatcher from "./MessageDispatcher";
import { TimeEdit } from "./TimeEdit";
import API from "./TimeEditAPI";
import { ERegion } from "@timeedit/types/lib/enums/region.enum";

import * as Sentry from "@sentry/browser";

export type TServerEnv = {
    platformVersion: string;
    serverEnv: string;
    serverRegion: ERegion;
    useDevDomainForBetaEnv: boolean;
};

const DO_NOT_SHOW_TIMEOUT_MESSAGES = [
    "setPreferences",
    "getCurrentDateTime",
    "getSystemBackgroundText",
    "setCurrentUserPrefs",
];

const DEV_TIMEOUT = 60000;
const STANDARD_TIMEOUT = 60000; // Increased 2025-03-24 (from 30000) while trying to handle timeout issues (https://timeedit.atlassian.net/browse/SCHED-1534)

const getSignatureFromToken = () => {
    try {
        const stored = window.localStorage.getItem("te_auth_token");
        if (stored) {
            const { accessToken } = JSON.parse(stored);
            if (accessToken) {
                const payload = JSON.parse(
                    atob(accessToken.split(".")[1].replace(/-/g, "+").replace(/_/g, "/"))
                );
                return payload.customerSignature?.replace(/^cloud_/, "");
            }
        }
        return null;
    } catch (ignore) {
        return null;
    }
};

// Replaces path.resolve("/", p): one leading slash, no trailing one.
// E.g. "sv/client/" becomes "/sv/client".
const toAbsolutePath = (appPath: string): string => {
    const normalized = `/${appPath}`.replace(/\/{2,}/g, "/");
    return normalized.length > 1 ? normalized.replace(/\/$/, "") : normalized;
};

export const setupServerConnection = (callback: (environment: TServerEnv) => void) => {
    const BASE_URL = window.TECORE_SERVER_URL?.startsWith("https")
        ? `${window.TECORE_SERVER_URL}/${getSignatureFromToken()}/client`
        : toAbsolutePath(window.TIMEEDIT_APP_PATH);

    // Force a slash at the end to play nice with nginx
    const apiTimeout = process.env.NODE_ENV === "development" ? DEV_TIMEOUT : STANDARD_TIMEOUT;
    const dispatcher = new MessageDispatcher(`${BASE_URL}/`, apiTimeout, () => {
        Sentry.captureMessage("Reloading, tecore-server returned 401");
        window.location.reload();
    });
    dispatcher.on("complete", () => {
        document.body.style.cursor = "auto";
    });

    type TEventListEvent = {
        customer_signature: string | null;
        req_function: string;
        start_time_epoch: number;
        application_type: "teclient";
        end_username: string;
        end_user_time: string;
        teserver_time: string;
        database_time: string;
        proxy_api_time: string;
        proxy_stalled_time: string;
        proxy_processing_time: string;
        server_type: string;
    };
    // Log messages
    let eventList: TEventListEvent[] = [];
    dispatcher.on("complete", (data) => {
        if (!data || !data.response || !data.response.time) {
            return;
        }

        const customer = TimeEdit.serverInfo ? TimeEdit.serverInfo.server.signature : null;
        const state = TimeEdit.State.get();
        const username = state && state.user ? state.user.login : "";
        const method = data.response.name;
        const time = data.response.time;
        eventList.push({
            customer_signature: customer,
            req_function: method,
            start_time_epoch: Date.now(),
            application_type: "teclient",
            end_username: username,

            end_user_time: time.rtt,
            teserver_time: time.server,
            database_time: time.database,
            proxy_api_time: time.proxy_api,
            proxy_stalled_time: time.proxy_stalled,
            proxy_processing_time: time.proxy_processing,
            server_type: data.response.type,
        });

        const EVENT_BATCH_SIZE = 20;
        if (eventList.length < EVENT_BATCH_SIZE) {
            return;
        }

        const eventData = {
            data: eventList,
        };

        if (process.env.NODE_ENV !== "production") {
            console.log("Logging API calls:", eventData);
        } else {
            const req = new XMLHttpRequest();
            const url = `${window.location.protocol}//${window.location.host}/v1/events`;
            req.open("post", url, true);
            req.setRequestHeader("Content-Type", "application/json");
            req.send(JSON.stringify(eventData));
        }
        eventList = [];
    });
    dispatcher.on("slowResponse", () => {
        document.body.style.cursor = "progress";
    });
    dispatcher.on("timeout", (data) => {
        Log.debug("Request timed out.", data);
        if (!data.handled) {
            if (!DO_NOT_SHOW_TIMEOUT_MESSAGES.includes(data.method)) {
                Log.error(Language.get("nc_api_connection_problem"), data);
            }

            const customer = TimeEdit.serverInfo ? TimeEdit.serverInfo.server.signature : null;
            const state = TimeEdit.State.get();
            const username = state && state.user ? state.user.login : "";
            const time = data?.response?.time || {
                rtt: -1,
                server: -1,
                database: -1,
                proxy_api: -1,
                proxy_stalled: -1,
                proxy_processing: -1,
            };
            eventList.push({
                customer_signature: customer,
                req_function: data.method,
                start_time_epoch: Date.now(),
                application_type: "teclient",
                end_username: username,

                end_user_time: time.rtt,
                teserver_time: time.server,
                database_time: time.database,
                proxy_api_time: time.proxy_api,
                proxy_stalled_time: time.proxy_stalled,
                proxy_processing_time: time.proxy_processing,
                server_type: "timeout",
            });
        }
    });

    API.setMessageDispatcher(dispatcher, Log);

    dispatcher.send(
        "getServerEnv",
        {},
        (envResult) => {
            if (envResult instanceof Error) {
                Log.error(`Server error , ${envResult.message}`);
                document.getElementById(
                    "core-container"
                )!.innerHTML = `<div><div title="Server error"><p>Could not get a response from the server.<br/>Please try reloading the page, or contact support if the problem persists.</p></div></div>`;

                return;
            }
            const environment = envResult.parameters[0];

            console.log("getServerEnv", environment);

            callback(environment);
        },
        false,
        (errorType, message) => {
            Log.error(`${errorType}, ${message}`);
            document.getElementById(
                "core-container"
            )!.innerHTML = `<div><div><p>Could not get a response from the server.<br/>Please try reloading the page, or contact support if the problem persists.</p></div></div>`;
        }
    );
};
